/**
 * Custom error types for domain-specific errors
 * Following TypeScript best practices for error handling
 */

/**
 * Base application error class
 */
export class AppError extends Error {
    public readonly timestamp: Date;

    constructor(message: string) {
        super(message);
        this.name = 'AppError';
        this.timestamp = new Date();
        Object.setPrototypeOf(this, new.target.prototype);
    }
}

/**
 * API-related errors with status code and endpoint information
 */
export class ApiError extends AppError {
    public readonly statusCode: number;
    public readonly endpoint: string;
    public readonly originalError?: unknown;

    constructor(
        message: string,
        statusCode: number,
        endpoint: string,
        originalError?: unknown
    ) {
        super(message);
        this.name = 'ApiError';
        this.statusCode = statusCode;
        this.endpoint = endpoint;
        this.originalError = originalError;
    }

    static fromAxiosError(error: unknown, endpoint: string): ApiError {
        if (error && typeof error === 'object' && 'response' in error) {
            const axiosError = error as { response?: { status?: number; data?: { message?: string } }; message?: string };
            const status = axiosError.response?.status ?? 500;
            const message = axiosError.response?.data?.message ?? axiosError.message ?? 'Unknown API error';
            return new ApiError(message, status, endpoint, error);
        }
        return new ApiError('Unknown API error', 500, endpoint, error);
    }
}

/**
 * Configuration-related errors
 */
export class ConfigurationError extends AppError {
    public readonly configKey?: string;

    constructor(message: string, configKey?: string) {
        super(message);
        this.name = 'ConfigurationError';
        this.configKey = configKey;
    }
}

/**
 * Validation errors for form or data validation
 */
export class ValidationError extends AppError {
    public readonly field?: string;
    public readonly value?: unknown;

    constructor(message: string, field?: string, value?: unknown) {
        super(message);
        this.name = 'ValidationError';
        this.field = field;
        this.value = value;
    }
}

/**
 * Store-related errors for MobX state management issues
 */
export class StoreError extends AppError {
    public readonly storeName: string;
    public readonly action?: string;

    constructor(message: string, storeName: string, action?: string) {
        super(message);
        this.name = 'StoreError';
        this.storeName = storeName;
        this.action = action;
    }
}
